import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest } from "../apiService/allapi";
import { successAlert, errorAlert } from "../apiService/sweetAlert";
import { isLoggedIn } from "../helper/cartUtils";

const Checkout = () => {
  const [selectedPayment, setSelectedPayment] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [shippingCharge, setShippingCharge] = useState(0);

  // 🔥 NEW ADDRESS STATES
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [isSavingAddress, setIsSavingAddress] = useState(false);

  // Razorpay states
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [razorpayOrderId, setRazorpayOrderId] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // Razorpay Key (use your live key in production)
  // const RAZORPAY_KEY_ID =  "rzp_test_SbM7J4o8Fuxt7e"; //
  const RAZORPAY_KEY_ID = "rzp_live_SiORq4iVpKfs7O"; //

  // Require login
  useEffect(() => {
    if (!isLoggedIn()) {
      errorAlert("Please login to checkout");
      navigate("/account");
    }
  }, [navigate]);

  const handlePaymentChange = (event) => {
    setSelectedPayment(event.target.id);
  };

  // 🔥 EXISTING CART FUNCTIONS (UNCHANGED)
  const getCartItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await apiRequest("/Cart/list", "GET", null, {
        Authorization: `Bearer ${token}`,
      });
      if (res?.data?.status === true) {
        setCartItems(res.data.data.data);
      }
    } catch (error) {
      console.log("Cart Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCartTotal = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await apiRequest("/Cart/total", "GET", null, {
        Authorization: `Bearer ${token}`,
      });
      if (res?.data?.status === true) {
        setTotalPrice(res.data.data.totalNetAmount);
        setShippingCharge(res.data.data.totalShippingCharges);
      }
    } catch (error) {
      console.log("Total API Error:", error);
    }
  };

  // 🔥 NEW ADDRESS FUNCTIONS
  const fetchAddresses = async () => {
    if (!token) {
      setAddresses([]);
      return;
    }
    try {
      const res = await apiRequest("/User/get-addresses", "GET", null, {
        Authorization: `Bearer ${token}`,
      });
      if (res?.data?.status === true) {
        const list = res.data.data || [];
        setAddresses(list);

        // Auto-select default address
        const defaultAddr = list.find((a) => a.isDefault);
        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.id);
          setSelectedAddress(defaultAddr);
        } else if (list.length > 0) {
          setSelectedAddressId(list[0].id);
          setSelectedAddress(list[0]);
        }
      }
    } catch (error) {
      console.error("Address fetch error:", error);
      setAddresses([]);
    }
  };

  const handleAddressChange = async (id) => {
    setSelectedAddressId(id);
    const addr = addresses.find((a) => a.id === id);
    setSelectedAddress(addr);

    try {
      await apiRequest(
        `/User/SetDefaultAddress?addressId=${id}`,
        "POST",
        null,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      // Sync with header
      window.dispatchEvent(new Event("addressUpdated"));
    } catch (error) {
      console.error("Set default error:", error);
    }
  };

  // 🔥 LOAD DATA ON MOUNT
  useEffect(() => {
    if (isLoggedIn()) {
      getCartItems();
      getCartTotal();
      fetchAddresses();
    }
  }, []);

  useEffect(() => {
    // Listen for global address updates (from User.jsx)
    const handleAddressUpdate = () => fetchAddresses();
    window.addEventListener("addressUpdated", handleAddressUpdate);
    return () =>
      window.removeEventListener("addressUpdated", handleAddressUpdate);
  }, []);
  // place order api
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";

      script.onload = () => {
        console.log("Razorpay SDK Loaded");
        resolve(true);
      };

      script.onerror = () => {
        console.log("Razorpay SDK Load Failed");
        resolve(false);
      };

      document.body.appendChild(script);
    });
  };

  const handleRazorpayPayment = async (orderIdFromBackend) => {
    try {
      setPaymentLoading(true);

      const sdkLoaded = await loadRazorpayScript();

      if (!sdkLoaded) {
        errorAlert("Razorpay SDK failed to load");
        return;
      }

      if (!orderIdFromBackend) {
        errorAlert("Razorpay Order ID not found");
        return;
      }

      const totalAmountPaise = Math.round(
        (Number(totalPrice) + Number(shippingCharge)) * 100,
      );

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: totalAmountPaise,
        currency: "INR",
        name: "Airdeal India",
        description: "Order Payment",
        order_id: orderIdFromBackend,

        handler: async function (response) {
          console.log("=================================");
          console.log("PAYMENT SUCCESS CALLBACK HIT");
          console.log("=================================");
          console.log("Razorpay Response:", response);

          try {
            const txnId = localStorage.getItem("transactionId");

            console.log("Transaction ID:", txnId);

            const verifyPayload = {
              TransactionId: txnId,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              errorDescription: "",
            };

            console.log("Verify Payload:");
            console.log(verifyPayload);

            console.log("Calling Verify API...");

            const verifyRes = await apiRequest(
              "/Order/verify-payment",
              "POST",
              verifyPayload,
              {
                Authorization: `Bearer ${token}`,
              },
            );

            console.log("Verify API Response:");
            console.log(verifyRes);

            if (verifyRes?.data?.status === 1) {
              console.log("Payment Verification Success");

              successAlert("Payment successful! Order confirmed.");

              localStorage.removeItem("transactionId");

              console.log("Navigating To Home Page");

              navigate("/");
            } else {
              console.log("Payment Verification Failed");
              console.log("Response Data:", verifyRes?.data);

              errorAlert("Payment verification failed");
            }
          } catch (verifyError) {
            console.error("Verify API Exception:", verifyError);

            if (verifyError?.response) {
              console.log("Error Response:", verifyError.response);
            }

            errorAlert("Payment failed. Contact support.");
          }
        },

        prefill: {
          name: selectedAddress?.name || "",
          contact: selectedAddress?.phone || "",
        },

        theme: {
          color: "#3399cc",
        },

        modal: {
          ondismiss: function () {
            console.log("Razorpay Modal Closed");
          },
        },
      };

      console.log("Razorpay Options:");
      console.log(options);

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        console.log("=================================");
        console.log("PAYMENT FAILED CALLBACK HIT");
        console.log("=================================");
        console.log(response);

        console.log("Error Code:", response?.error?.code);
        console.log("Error Description:", response?.error?.description);
        console.log("Error Source:", response?.error?.source);
        console.log("Error Step:", response?.error?.step);
        console.log("Error Reason:", response?.error?.reason);
      });

      console.log("Opening Razorpay Checkout");

      rzp.open();
    } catch (error) {
      console.error("Razorpay Initialization Error:", error);

      if (error?.response) {
        console.log("API Error Response:", error.response);
      }

      errorAlert("Payment initialization failed");
    } finally {
      console.log("Payment Loading False");
      setPaymentLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      errorAlert("Please select address");
      return;
    }
    if (!selectedPayment) {
      errorAlert("Please select a payment method");
      return;
    }

    if (cartItems.length === 0) {
      errorAlert("Your cart is empty");
      return;
    }

    try {
      setLoading(true);

      const paymentMode = selectedPayment === "payment4" ? "ONLINE" : "COD";
      const payload = {
        userId: 0,
        addressId: Number(selectedAddressId),
        paymentMode,
      };

      const res = await apiRequest("/Order/Place-Order", "POST", payload, {
        Authorization: `Bearer ${token}`,
      });

      if (res?.data?.success) {
        // ✅ yahi important line hai
        const txnId = res.data.transactionID; // ✅ correct key

        if (txnId) {
          localStorage.setItem("transactionId", txnId);
        } else {
          console.log("TransactionId NOT FOUND ❌");
        }

        if (selectedPayment === "payment4") {
          // Razorpay flow
          console.log("Place Order Response", res.data);

          const razorpayOrderId = res.data.razorpayOrderId || res.data.orderId;

          if (!razorpayOrderId) {
            errorAlert("Razorpay Order ID not received");
            return;
          }

          await handleRazorpayPayment(razorpayOrderId);
        } else {
          // COD/Other - current flow
          successAlert(res.data.message || "Order placed successfully");
          navigate("/");
        }
      } else {
        errorAlert(res.data.message || "Order failed");
      }
    } catch (error) {
      console.log("Order Error:", error);
      errorAlert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="checkout py-80">
      <div className="container container-lg">
        <div className="border border-gray-100 rounded-8 px-30 py-20 mb-40">
          <span className="">
            Have a coupon?{" "}
            <Link
              to="/cart"
              className="fw-semibold text-gray-900 hover-text-decoration-underline hover-text-main-600"
            >
              Click here to enter your code
            </Link>{" "}
          </span>
        </div>

        <div className="row">
          <div className="col-xl-9 col-lg-8">
            {/* 🔥 NEW ADDRESS SECTION */}
            <div className="border border-gray-100 rounded-8 p-24 mb-32">
              <h5 className="fw-bold mb-20">Delivery Address</h5>

              {selectedAddress ? (
                <>
                  {/* Selected Address Preview */}
                  <div className="bg-gray-50 p-20 rounded-8 mb-20">
                    <div className="d-flex justify-content-between align-items-start mb-12">
                      <h6 className="fw-semibold mb-4">
                        {selectedAddress.name}
                      </h6>
                      {selectedAddress.isDefault && (
                        <span className="badge bg-success">Default</span>
                      )}
                    </div>
                    <p className="mb-8">
                      {selectedAddress.address},{" "}
                      {selectedAddress.cityName || selectedAddress.city},
                      {selectedAddress.stateName || selectedAddress.state}{" "}
                      {selectedAddress.pinCode || selectedAddress.pincode}
                    </p>
                    <p className="mb-0 text-muted small">
                      {selectedAddress.phone || selectedAddress.mobile}
                    </p>
                  </div>

                  {/* Address Selector */}
                  <div className="row g-3 mb-20">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold mb-8">
                        Use Address
                      </label>
                      <select
                        className="form-select common-input"
                        value={selectedAddressId}
                        onChange={(e) => handleAddressChange(e.target.value)}
                      >
                        <option value="">Select Address</option>
                        {addresses.map((addr) => (
                          <option key={addr.id} value={addr.id}>
                            {addr.name} - {addr.address?.substring(0, 30)}...
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-40">
                  <i className="ph ph-map-pin-line text-4xl text-gray-300 mb-16" />
                  <h6>No addresses found</h6>
                  <p className="text-muted mb-20">
                    Add your delivery address to continue
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddressModal(true)}
                  >
                    Add Address
                  </button>
                </div>
              )}
            </div>

            {/* Payment Method (UNCHANGED) */}
            <div>
              <h6 className="text-lg mb-24">Payment Method</h6>
              {/* <div className="payment-item">
                  <div className="form-check common-check common-radio py-16 mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="payment"
                      id="payment1"
                      checked={selectedPayment === "payment1"}
                      onChange={handlePaymentChange}
                    />
                    <label
                      className="form-check-label fw-semibold text-neutral-600"
                      htmlFor="payment1"
                    >
                      Direct Bank transfer
                    </label>
                  </div>
                  {selectedPayment === "payment1" && (
                    <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                      <p className="text-gray-800">
                        Make your payment directly into our bank account. Please
                        use your Order ID as the payment reference. Your order
                        will not be shipped until the funds have cleared in our
                        account.
                      </p>
                    </div>
                  )}
                </div> */}
              {/* ... other payment methods unchanged ... */}
              {/* <div className="payment-item">
                  <div className="form-check common-check common-radio py-16 mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="payment"
                      id="payment2"
                      checked={selectedPayment === "payment2"}
                      onChange={handlePaymentChange}
                    />
                    <label
                      className="form-check-label fw-semibold text-neutral-600"
                      htmlFor="payment2"
                    >
                      Check payments
                    </label>
                  </div>
                  {selectedPayment === "payment2" && (
                    <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                      <p className="text-gray-800">
                        Please send your cheque to our office address.
                      </p>
                    </div>
                  )}
                </div> */}

              {/* Razorpay Payment */}
              <div className="payment-item">
                <div className="form-check common-check common-radio py-16 mb-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment"
                    id="payment4"
                    checked={selectedPayment === "payment4"}
                    onChange={handlePaymentChange}
                  />
                  <label
                    className="form-check-label fw-semibold text-neutral-600"
                    htmlFor="payment4"
                  >
                    (Card/UPI/Netbanking)
                  </label>
                </div>
                {selectedPayment === "payment4" && (
                  <div className="payment-item__content px-16 py-24 rounded-8 bg-main-50 position-relative d-block">
                    <p className="text-gray-800 mb-12">
                      Secure payment via (Cards, UPI, Wallets, Netbanking)
                    </p>
                    <div className="flex-align flex-wrap gap-8 small text-success">
                      <i className="ph ph-lock-simple" />
                      <span>100% Secure</span>
                      <i className="ph ph-check-circle" />
                      <span>Instant Confirmation</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="payment-item">
                <div className="form-check common-check common-radio py-16 mb-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment"
                    id="payment3"
                    checked={selectedPayment === "payment3"}
                    onChange={handlePaymentChange}
                  />
                  <label
                    className="form-check-label fw-semibold text-neutral-600"
                    htmlFor="payment3"
                  >
                    Cash on delivery
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* 🔥 ADDRESS MODAL */}
          {showAddressModal && (
            <div
              className="modal fade show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
              tabIndex="-1"
            >
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {isEditMode ? "Edit Address" : "Add New Address"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowAddressModal(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="col-xl-3 col-lg-4">
            {/* Cart Summary - UNCHANGED */}
            <div className="checkout-sidebar">
              <div className="bg-color-three rounded-8 p-24 text-center">
                <span className="text-gray-900 text-xl fw-semibold">
                  Your Orders
                </span>
              </div>
              <div className="border border-gray-100 rounded-8 px-24 py-40 mt-24">
                {loading ? (
                  <p>Loading cart...</p>
                ) : cartItems.length === 0 ? (
                  <p>Your cart is empty</p>
                ) : (
                  <>
                    <div className="mb-32 pb-32 border-bottom border-gray-100 flex-between gap-8">
                      <span className="text-gray-900 fw-medium text-xl font-heading-two">
                        Product
                      </span>
                      <span className="text-gray-900 fw-medium text-xl font-heading-two">
                        Subtotal
                      </span>
                    </div>
                    {cartItems.map((item) => (
                      <div key={item.pid} className="flex-between gap-24 mb-32">
                        <div className="flex-align gap-12">
                          <span className="text-gray-900 fw-normal text-md font-heading-two w-144">
                            {item.productName}
                          </span>
                          <span className="text-gray-900 fw-normal text-md font-heading-two">
                            <i classNameName="ph-bold ph-x" />
                          </span>
                          <span className="text-gray-900 fw-semibold text-md font-heading-two">
                            {item.qty}
                          </span>
                        </div>
                        <span className="text-gray-900 fw-bold text-md font-heading-two">
                          ₹{item.itemWiseTotal}
                        </span>
                      </div>
                    ))}
                    <div className="border-top border-gray-100 pt-30 mt-30">
                      <div className="mb-32 flex-between gap-8">
                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                          Subtotal
                        </span>
                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                          ₹{totalPrice}
                        </span>
                      </div>
                      <div className="mb-32 flex-between gap-8">
                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                          Shipping
                        </span>
                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                          ₹{shippingCharge}
                        </span>
                      </div>
                      <div className="mb-0 flex-between gap-8">
                        <span className="text-gray-900 font-heading-two text-xl fw-semibold">
                          Total
                        </span>
                        <span className="text-gray-900 font-heading-two text-md fw-bold">
                          ₹{totalPrice + shippingCharge}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={handlePlaceOrder}
                className="btn btn-main mt-40 py-18 w-100 rounded-8 mt-56"
                disabled={!selectedAddress || loading || paymentLoading}
              >
                {paymentLoading
                  ? "Processing Payment..."
                  : loading
                    ? "Placing Order..."
                    : !selectedAddress
                      ? "Select Address First"
                      : "Place Order"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;
